import { DrawDetail, fmtMoney } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";
import Skeleton from "Components/Common/Skeleton/Skeleton";

interface StatusMeta {
  cls: string;
  label: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  approved: { cls: "approved", label: "✓ Approved & Funded" },
  cancelled: { cls: "rejected", label: "✕ Cancelled" },
  submitted: { cls: "pending", label: "⏳ Pending Bank Approval" },
  lender_action: { cls: "pending", label: "⏳ Pending Lender Approval" },
  inspection_pending: { cls: "pending", label: "⏳ Inspection Pending" },
  owner_signature_pending: {
    cls: "pending",
    label: "⏳ Awaiting Owner Signature",
  },
  draft: { cls: "pending", label: "Draft" },
};

interface DrawHeaderProps {
  projectName?: string;
  drawDetail: DrawDetail | null;
  loading: boolean;
}

const DrawHeader = ({ projectName, drawDetail, loading }: DrawHeaderProps) => {
  if (loading || !drawDetail) {
    return (
      <div className="drawHeader">
        <div className="dhLeft">
          <Skeleton
            width={150}
            height={20}
            borderRadius={20}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            width={260}
            height={22}
            borderRadius={4}
            style={{ marginBottom: 8 }}
          />
          <Skeleton width={200} height={13} borderRadius={4} />
        </div>
        <div className="dhRight">
          <Skeleton
            width={110}
            height={11}
            borderRadius={4}
            style={{ display: "inline-block", marginBottom: 6 }}
          />
          <Skeleton
            width={130}
            height={26}
            borderRadius={4}
            style={{ display: "block", marginLeft: "auto", marginBottom: 6 }}
          />
          <Skeleton
            width={90}
            height={11}
            borderRadius={4}
            style={{ display: "inline-block" }}
          />
        </div>
      </div>
    );
  }

  const { draw, summary, signatures, approval } = drawDetail;
  const meta = STATUS_META[draw.status ?? ""] ?? {
    cls: "pending",
    label: draw.status ?? "—",
  };
  const certifiedAmount = draw.approvedAmount ?? draw.requestedAmount;
  const contractSumToDate = summary?.contractSumToDate?.amount;
  const pctOfContract =
    contractSumToDate && certifiedAmount?.amount != null
      ? `${((certifiedAmount.amount / contractSumToDate) * 100).toFixed(1)}% of contract to date`
      : null;
  const gcName = signatures?.gc?.name;
  const bankName =
    approval?.approvedBy?.bankName || approval?.approvedBy?.branchName;

  return (
    <div className="drawHeader">
      <div className="dhLeft">
        <div className={`dhBadge ${meta.cls}`}>{meta.label}</div>
        <div className="dhTitle">
          Draw #{String(draw.drawNumber).padStart(2, "0")}
          {projectName ? ` — ${projectName}` : ""}
        </div>
        <div className="dhSub">
          {[
            bankName,
            draw.toDate
              ? `Period ending ${formatDate(draw.toDate, false)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          {gcName && (
            <>
              <br />
              General Contractor: {gcName}
            </>
          )}
        </div>
      </div>
      <div className="dhRight">
        <div className="dhAmountLabel">Payment Certified</div>
        <div className="dhAmount">{fmtMoney(certifiedAmount)}</div>
        {pctOfContract && <div className="dhAmountSub">{pctOfContract}</div>}
      </div>
    </div>
  );
};

export default DrawHeader;
